import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FieldValues, Path, PathValue, useFormContext } from 'react-hook-form';
import { MdSearch } from 'react-icons/md';
import { formatUserSearchResult } from '../../../views/cs/review/common/util/util';
import './SearchBar.scss';

export type SearchResult = {
  id: number;
  name: string;
  cellPhone?: string;
};

type SearchBarProps<TFormValues extends FieldValues> = {
  list: SearchResult[];
  onHandleSearch: (searchValue: string) => void;
  name: Path<TFormValues>;
  label?: string;
  defaultUserId?: number;
};

const SearchBar = <TFormValues extends FieldValues>({
  list = [],
  onHandleSearch,
  name,
  defaultUserId,
}: SearchBarProps<TFormValues>): JSX.Element => {
  const [searchValue, setSearchValue] = useState('');
  const [selected, setSelected] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { register, setValue, formState, clearErrors } = useFormContext<TFormValues>();
  const { errors } = formState;
  const hasError = errors && errors[name];

  const handleSelect = (result: SearchResult) => {
    if (errors && errors[name]) {
      clearErrors(name);
    }
    setSearchValue(result.name || '-');
    setSelected(true);
    setValue(name, result as PathValue<TFormValues, Path<TFormValues>>);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setSelected(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (searchInputRef.current && !searchInputRef.current.contains(event.target as HTMLElement)) {
      if (!selected) {
        setSearchValue('');
      }
    }
  };

  useEffect(() => {
    if (searchValue !== '' && !selected) {
      onHandleSearch(searchValue);
    }
  }, [searchValue, selected]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selected]);

  useEffect(() => {
    // defaultUserId가 제공되었을 때만 실행
    if (defaultUserId) {
      // 검색 결과 목록에서 초기 검색어와 일치하는 항목을 찾습니다.
      const matchedUser = list.find((user) => user.id === defaultUserId);
      // 일치하는 항목이 있는 경우, 해당 결과를 자동으로 선택합니다.
      if (matchedUser) {
        setSearchValue(matchedUser.name);
        setSelected(true);
        setValue(name, matchedUser as PathValue<TFormValues, Path<TFormValues>>);
      }
    }
  }, [list, defaultUserId]);

  return (
    <div className="search-bar" ref={searchInputRef}>
      <input
        type="text"
        autoComplete="off"
        placeholder="검색어를 입력하세요"
        value={searchValue || ''}
        {...(register && register(name, { required: true }))}
        onChange={handleSearch}
        className={classNames({ 'error-border': hasError })}
      />
      {list?.length > 0 && !selected && searchValue !== '' ? (
        <ul>
          {list?.map((result: SearchResult) => (
            <li key={result.id} onClick={() => handleSelect(result)}>
              {result.id && result.cellPhone ? formatUserSearchResult(result) : result.name || '-'}
            </li>
          ))}
        </ul>
      ) : !selected && searchValue !== '' ? (
        <div className="empty-search-result">
          <MdSearch size={24} />
          <p>검색 결과가 없습니다.</p>
        </div>
      ) : null}
      <p className="error-message">{hasError && !searchValue ? '검색어를 입력해주세요!' : null}</p>
    </div>
  );
};

export default SearchBar;
